import { useMemo } from 'react';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import type { GeoPermissibleObjects } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — vite handles JSON imports natively; no type defs ship for this file
import worldTopo from 'world-atlas/countries-110m.json';
import { aggregateByCountry } from '../../lib/countryNormalize';

/**
 * Geographic Threat Map.
 *
 * Renders a Natural Earth choropleth: every country shipped in the
 * world-atlas TopoJSON gets shaded by the volume of threat activity
 * attributed to it. The home country (e.g. "Malaysia" for the default MSSP
 * region) is rendered in a distinct neutral colour so the map clearly
 * shows "everyone else attacking us".
 *
 * The component is intentionally print-friendly: pure inline SVG, no
 * tile/image background, no JS animations. Width is responsive; height
 * is locked to a 2:1 aspect ratio that suits the Natural Earth projection.
 *
 * Inputs are aggregated client-side and the country names are normalised
 * (see `lib/countryNormalize.ts`) so we can take 2-letter ISO codes,
 * 3-letter ISO codes, and free-text country strings interchangeably.
 */

interface CountryRow { country: string; count: number }

interface Props {
  /** Raw country rows — duplicates and inconsistent spellings are fine. */
  data: ReadonlyArray<CountryRow>;
  /** The MSSP / customer's home country. Rendered in a neutral tone so the
   *  threat colours stand out. Pass either an ISO-2 ("MY") or a name. */
  homeCountry?: string;
  /** Title rendered above the map. */
  title?: string;
  /** Subtitle / description rendered just below the title. */
  subtitle?: string;
}

// ── World atlas (precomputed once) ──────────────────────────────────────────
type CountryFeatureProps = { name?: string };
type CountryFeature = Feature<Geometry, CountryFeatureProps>;
type WorldFC = FeatureCollection<Geometry, CountryFeatureProps>;
const WORLD: WorldFC = (() => {
  const topo = worldTopo as unknown as Parameters<typeof feature>[0];
  // The world-atlas TopoJSON uses object key "countries".
  return feature(
    topo,
    (topo as unknown as { objects: { countries: unknown } }).objects.countries as never,
  ) as unknown as WorldFC;
})();

// ── Colour ramp (5 buckets, sequential reds → safe in print) ────────────────
const RAMP = ['#fee5d9', '#fcae91', '#fb6a4a', '#de2d26', '#a50f15'] as const;
const ZERO_FILL = '#e5e7eb';
const HOME_FILL = '#cbd5e1';
const STROKE = '#ffffff';

function bucketFor(count: number, max: number): number {
  if (count <= 0 || max <= 0) return -1;
  // Log-scale buckets so a single huge attacker doesn't flatten everyone else.
  const ratio = Math.log10(count + 1) / Math.log10(max + 1);
  return Math.min(RAMP.length - 1, Math.max(0, Math.floor(ratio * RAMP.length)));
}

export function GeoThreatMap({ data, homeCountry, title, subtitle }: Props) {
  // Aggregate + normalise the input rows. Output is sorted desc by count.
  const aggregated = useMemo(() => aggregateByCountry(
    data.map(d => ({ country: String(d.country ?? ''), count: Number(d.count ?? 0) })),
  ), [data]);

  const countByCountry = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of aggregated) m.set(r.country.toLowerCase(), r.count);
    return m;
  }, [aggregated]);

  const max = aggregated[0]?.count ?? 0;
  const totalEvents = aggregated.reduce((acc, r) => acc + r.count, 0);
  const totalCountries = aggregated.length;
  const top5 = aggregated.slice(0, 5);

  // Normalise the home country once.
  const homeName = useMemo(() => {
    if (!homeCountry) return '';
    // Inline a tiny normalise — just lowercase + ISO-2 fast path. Falls back
    // to the alias map by going through aggregateByCountry against a stub.
    const stub = aggregateByCountry([{ country: homeCountry, count: 1 }]);
    return (stub[0]?.country ?? homeCountry).toLowerCase();
  }, [homeCountry]);

  // Project once; the SVG path-d strings are deterministic from the projection.
  const { width, height, paths } = useMemo(() => {
    const w = 800;
    const h = 400;
    const projection = geoNaturalEarth1().fitSize([w, h], WORLD as unknown as GeoPermissibleObjects);
    const pathGen = geoPath(projection);
    type RenderedPath = { d: string; name: string; count: number; fill: string; isHome: boolean; key: number };
    const out: RenderedPath[] = WORLD.features.map((f: CountryFeature, i: number) => {
      const name = (f.properties?.name ?? '').toLowerCase();
      const d = pathGen(f as unknown as GeoPermissibleObjects) ?? '';
      const isHome = Boolean(name) && name === homeName;
      const count = countByCountry.get(name) ?? 0;
      const bucket = isHome ? -2 : bucketFor(count, max);
      const fill = isHome ? HOME_FILL
                 : bucket < 0 ? ZERO_FILL
                 : RAMP[bucket];
      return { d, name: f.properties?.name ?? '', count, fill, isHome, key: i };
    }).filter((p: RenderedPath) => p.d);
    return { width: w, height: h, paths: out };
  }, [countByCountry, homeName, max]);

  const empty = aggregated.length === 0;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        padding: '18px 22px 16px',
        boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05em', fontWeight: 700, color: '#0f172a' }}>
            {title ?? 'Threat Origin Map'}
          </h3>
          {subtitle && (
            <p style={{ margin: '2px 0 0', fontSize: '0.82em', color: '#64748b' }}>{subtitle}</p>
          )}
        </div>
        <div style={{ fontSize: '0.78em', color: '#475569' }}>
          {totalEvents.toLocaleString()} events from {totalCountries} {totalCountries === 1 ? 'country' : 'countries'}
        </div>
      </div>

      {empty ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: '0.9em' }}>
          No country-level threat data available for this period.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2.5fr) minmax(180px, 1fr)', gap: 16, alignItems: 'start' }}>
          {/* Map */}
          <div style={{ minWidth: 0 }}>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              role="img"
              aria-label="World map shaded by threat activity per country"
              style={{ width: '100%', height: 'auto', display: 'block', background: '#f8fafc', borderRadius: 8 }}
            >
              {paths.map((p: { key: number; d: string; fill: string; name: string; count: number; isHome: boolean }) => (
                <path
                  key={p.key}
                  d={p.d}
                  fill={p.fill}
                  stroke={STROKE}
                  strokeWidth={0.4}
                >
                  <title>
                    {p.name}{p.isHome ? ' (home)' : ''}{p.count > 0 ? `: ${p.count.toLocaleString()} events` : ''}
                  </title>
                </path>
              ))}
            </svg>
            {/* Legend */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 8, fontSize: '0.72em', color: '#64748b' }}>
              <span>Fewer events</span>
              <div style={{ display: 'flex', gap: 1 }}>
                {RAMP.map((c, i) => (
                  <span key={i} style={{ width: 16, height: 10, background: c, display: 'inline-block' }} aria-hidden="true" />
                ))}
              </div>
              <span>More events</span>
              {homeName && (
                <>
                  <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, background: HOME_FILL, display: 'inline-block', borderRadius: 2 }} aria-hidden="true" /> home
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Top countries side panel */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              Top Sources
            </div>
            <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {top5.map((row, i) => {
                const pct = totalEvents > 0 ? (row.count / totalEvents) * 100 : 0;
                return (
                  <li key={row.country} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '0.85em', color: '#0f172a' }}>
                      <span style={{ fontWeight: 600 }}>{i + 1}. {row.country}</span>
                      <span style={{ color: '#475569', fontVariantNumeric: 'tabular-nums' }}>{row.count.toLocaleString()}</span>
                    </div>
                    <div style={{ position: 'relative', height: 6, background: '#f1f5f9', borderRadius: 3, marginTop: 4, overflow: 'hidden' }}>
                      <div
                        style={{
                          position: 'absolute', left: 0, top: 0, bottom: 0,
                          width: `${Math.max(2, Math.min(100, pct))}%`,
                          background: RAMP[Math.max(0, RAMP.length - 1 - i)],
                          borderRadius: 3,
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ol>
            {totalCountries > 5 && (
              <div style={{ fontSize: '0.75em', color: '#94a3b8', marginTop: 6 }}>
                + {totalCountries - 5} more {totalCountries - 5 === 1 ? 'country' : 'countries'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
